import ReferenceCurrency from '../components/ReferenceCurrency.jsx';
// src/pages/BookingPage.jsx
// Migrated from prototype/between-the-lines-v2.html lines 9186-9370.
// Modifications: 2 setPage calls converted to navigate (Sprint 2c-1 setPage->navigate
// pattern from Sprint 2b-1 / 2b-2). Otherwise verbatim.

import { track } from '../analytics/analytics.js';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { IconCheck } from '../components/icons.jsx';
import { DEFAULT_BOOKING } from '../config/booking.js';
import { db, AUTH_MODE } from '../auth/firebase.js';
import { useAuth } from '../auth/useAuth.js';
import { bookingPrice } from './bookingDisplay.js';

export const BookingPage = ({ state }) => {
    const navigate = useNavigate();
    const booking = { ...DEFAULT_BOOKING, ...(state.bookingContent || {}) };
    const [sessionType, setSessionType] = useState('one-on-one');
    const [form, setForm] = useState({
        name: state.user?.name || '',
        email: state.user?.email || '',
        phone: '',
        preferredDate: '',
        preferredTime: '',
        topic: '',
        groupContacts: ''
    });
    const { currentUser } = useAuth();
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Write a /serviceRequests doc (Slice A). The owner may only set the 5 allowlisted
    // fields with status:'requested'; the admin CFs add quote/bill/paid/etc. later.
    const onSubmit = async () => {
        setError(null);
        if (AUTH_MODE === 'mock' || !db) {
            setError('Booking requests need the live app (emulator or firebase mode), not mock mode.');
            return;
        }
        const uid = currentUser?.uid;
        if (!uid) { setError('Please sign in to request a session.'); return; }
        setSubmitting(true);
        try {
            const payload = {
                sessionType,
                name: form.name,
                email: form.email,
                phone: form.phone || '',
                preferredDate: form.preferredDate,
                preferredTime: form.preferredTime || '',
                topic: form.topic || '',
                groupContacts: sessionType === 'small-group' ? (form.groupContacts || '') : ''
            };
            await addDoc(collection(db, 'serviceRequests'), {
                type: 'booking',
                uid,
                status: 'requested',
                payload,
                createdAt: serverTimestamp()
            });
            track('generate_lead', { lead_type: 'booking' });
            setSubmitted(true);
        } catch (e) {
            setError((e && e.message) || 'Could not submit your request. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="page-fade-in min-h-[calc(100vh-4rem)] bg-leather-bg flex items-center justify-center px-4 py-12">
                <div className="max-w-lg w-full text-center border border-leather-accent/30 bg-leather-page/60 p-12 decorative-corner text-leather-accent">
                    <div className="w-16 h-16 rounded-full border-2 border-leather-accent flex items-center justify-center mx-auto mb-6">
                        <IconCheck className="w-8 h-8 text-leather-accent" />
                    </div>
                    <h2 className="font-display text-3xl text-leather-text mb-4">{booking.confirmationTitle || 'Request received'}</h2>
                    <p className="font-serif text-leather-muted mb-8 leading-relaxed">
                        {booking.confirmationMessage || 'Danielle will review your request and send a quote. Check Account → My Bookings to accept and pay.'}
                    </p>
                    <div className="flex gap-3 justify-center">
                        <button onClick={() => navigate('/account')}
                            className="px-5 py-3 border border-leather-accent text-leather-accent font-display text-xs hover:bg-leather-accent/10 transition-colors font-semibold">
                            View My Bookings
                        </button>
                        <button onClick={() => navigate('/dashboard')}
                            className="px-5 py-3 bg-leather-accent text-leather-pageDark font-display text-xs hover:bg-leather-accentLight transition-colors btn-sweep font-semibold">
                            Back to Library
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-fade-in min-h-[calc(100vh-4rem)] bg-leather-bg paper-texture page-root-text-leather">
            <div className="max-w-5xl mx-auto px-4 md:px-8 py-10 md:py-16">
                {/* Header */}
                <div className="mb-10">
                    <div className="font-display text-leather-accent text-xs mb-2 font-semibold">{booking.pageTagline || 'Live teaching with Danielle'}</div>
                    <h1 className="font-display text-3xl md:text-5xl text-leather-text mb-2">{booking.pageTitle || 'Book a session'}</h1>
                    <p className="font-serif italic text-leather-muted text-lg">{booking.pageSubtitle}</p>
                    <p className="mt-3 text-sm text-leather-muted">Consultations take place online via Zoom. We will share your meeting link in your booking messages before the session.</p>
                    <p className="mt-3 text-sm text-leather-muted">Request a session, receive Danielle’s quote, then pay in your account by card through Stripe or PayMe / FPS. Confirm the date and time with Danielle before paying.</p>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                    {/* LEFT — Form */}
                    <div className="md:col-span-2 space-y-5">
                        {/* Session type */}
                        <div className="border border-leather-accent/20 bg-leather-page/60 p-5 decorative-corner text-leather-accent">
                            <h3 className="font-display text-leather-accent text-xs mb-4 font-semibold">Session Type</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <button onClick={() => setSessionType('one-on-one')}
                                    className={`p-4 border text-left transition-all ${sessionType === 'one-on-one' ? 'border-leather-accent bg-leather-accent/10' : 'border-leather-accent/20 hover:border-leather-accent/50'}`}>
                                    <div className="font-display text-leather-text text-sm mb-1 font-semibold">{booking.oneOnOneLabel || 'One-to-one'}</div>
                                    <div className="font-serif italic text-leather-muted text-xs mb-2">{booking.oneOnOneDuration}</div>
                                    <div className="font-display text-leather-accent">{bookingPrice(booking.oneOnOnePrice)}</div>
                                </button>
                                <button onClick={() => setSessionType('small-group')}
                                    className={`p-4 border text-left transition-all ${sessionType === 'small-group' ? 'border-leather-accent bg-leather-accent/10' : 'border-leather-accent/20 hover:border-leather-accent/50'}`}>
                                    <div className="font-display text-leather-text text-sm mb-1 font-semibold">{booking.smallGroupLabel || 'Small group'}{booking.smallGroupMaxSize ? ` (${booking.smallGroupMaxSize})` : ''}</div>
                                    <div className="font-serif italic text-leather-muted text-xs mb-2">{booking.smallGroupDuration}</div>
                                    <div className="font-display text-leather-accent">{bookingPrice(booking.smallGroupPrice, true)}</div>
                                </button>
                            </div>
                        </div>

                        {/* Your Details */}
                        <div className="border border-leather-accent/20 bg-leather-page/60 p-5 text-leather-accent">
                            <h3 className="font-display text-leather-accent text-xs mb-4 font-semibold">Your Details</h3>
                            <div className="grid md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-xs font-display text-leather-muted mb-2 font-semibold">Name</label>
                                    <input type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                                        className="w-full bg-leather-pageDark border border-leather-accent/30 px-3 py-2.5 text-leather-text font-serif focus:outline-none focus:border-leather-accent" />
                                </div>
                                <div>
                                    <label className="block text-xs font-display text-leather-muted mb-2 font-semibold">Email</label>
                                    <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                                        className="w-full bg-leather-pageDark border border-leather-accent/30 px-3 py-2.5 text-leather-text font-serif focus:outline-none focus:border-leather-accent" />
                                </div>
                            </div>
                            <div className="mb-4">
                                <label className="block text-xs font-display text-leather-muted mb-2 font-semibold">Phone (optional)</label>
                                <input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                                    className="w-full bg-leather-pageDark border border-leather-accent/30 px-3 py-2.5 text-leather-text font-serif focus:outline-none focus:border-leather-accent" />
                            </div>
                            <div className="grid md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-xs font-display text-leather-muted mb-2 font-semibold">Preferred Date</label>
                                    <input type="date" value={form.preferredDate} onChange={(e) => setForm({...form, preferredDate: e.target.value})}
                                        className="w-full bg-leather-pageDark border border-leather-accent/30 px-3 py-2.5 text-leather-text font-serif focus:outline-none focus:border-leather-accent" />
                                </div>
                                <div>
                                    <label className="block text-xs font-display text-leather-muted mb-2 font-semibold">Preferred Time</label>
                                    <input type="time" value={form.preferredTime} onChange={(e) => setForm({...form, preferredTime: e.target.value})}
                                        className="w-full bg-leather-pageDark border border-leather-accent/30 px-3 py-2.5 text-leather-text font-serif focus:outline-none focus:border-leather-accent" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-display text-leather-muted mb-2 font-semibold">What would you like to focus on?</label>
                                <textarea value={form.topic} onChange={(e) => setForm({...form, topic: e.target.value})} rows="4"
                                    placeholder="e.g. Paper 1 structure, specific anthology text, exam technique..."
                                    className="w-full bg-leather-pageDark border border-leather-accent/30 px-3 py-2.5 text-leather-text font-serif focus:outline-none focus:border-leather-accent resize-none"></textarea>
                            </div>
                            {sessionType === 'small-group' && (
                                <div className="mt-4">
                                    <label className="block text-xs font-display text-leather-muted mb-2 font-semibold">Group members (names &amp; emails, one per line)</label>
                                    <textarea value={form.groupContacts} onChange={(e) => setForm({...form, groupContacts: e.target.value})} rows="3"
                                        placeholder={"e.g. Alex Chan — alex@example.com\nJamie Lee — jamie@example.com"}
                                        className="w-full bg-leather-pageDark border border-leather-accent/30 px-3 py-2.5 text-leather-text font-serif focus:outline-none focus:border-leather-accent resize-none"></textarea>
                                    <p className="font-serif italic text-leather-muted text-xs mt-1.5">Danielle will use this to confirm group size when sending your quote.</p>
                                </div>
                            )}
                        </div>

                        {error && <p className="font-serif text-red-300 text-sm border border-red-500/40 bg-red-500/10 p-3">{error}</p>}
                        <button onClick={onSubmit} disabled={submitting || !form.name || !form.email || !form.preferredDate}
                            className="w-full py-3 bg-leather-accent text-leather-pageDark font-display text-sm hover:bg-leather-accentLight transition-colors btn-sweep disabled:opacity-30 disabled:cursor-not-allowed font-semibold">
                            {submitting ? 'Submitting…' : 'Request a session'}
                        </button>
                        <p className="font-serif text-leather-muted text-xs leading-relaxed">No payment at this step. Danielle will confirm availability and your quote before you accept and pay in Account → My Bookings.</p>
                    </div>

                    {/* RIGHT — Pricing summary */}
                    <div>
                        <div className="sticky top-24 border border-leather-accent/30 bg-leather-pageDark p-5 decorative-corner text-leather-accent">
                            <ReferenceCurrency amount={Number(sessionType === 'one-on-one' ? booking.oneOnOnePrice : booking.smallGroupPrice)} />
                            <h3 className="font-display text-leather-accent text-xs mb-4 font-semibold">Pricing</h3>
                            <div className="space-y-4">
                                <div>
                                    <div className="font-display text-leather-text text-sm mb-1 font-semibold">{booking.oneOnOneDurationSummary || 'One-to-one'}</div>
                                    <div className="font-display text-leather-accent text-2xl mb-1">{bookingPrice(booking.oneOnOnePrice)}</div>
                                    <p className="font-serif italic text-leather-muted text-xs">{booking.oneOnOneDescription}</p>
                                </div>
                                <div className="ornamental-divider text-leather-accent"></div>
                                <div>
                                    <div className="font-display text-leather-text text-sm mb-1 font-semibold">{booking.smallGroupDurationSummary || 'Small group'}</div>
                                    <div className="font-display text-leather-accent text-2xl mb-1">{bookingPrice(booking.smallGroupPrice, true)}</div>
                                    <p className="font-serif italic text-leather-muted text-xs">{booking.smallGroupDescription}</p>
                                </div>
                                <div className="ornamental-divider text-leather-accent"></div>
                                <p className="font-serif italic text-leather-muted text-xs">
                                    {booking.zoomNote} {booking.cancellationPolicy}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BookingPage;
